<template>
  <div class="smart-input" :class="classList">
    <label :class="{'is-example': exampleMessage}">
      <span class="placeholder"
        >{{ placeholder }}
        <span v-if="required" class="required">*</span>
      </span>
      <input
        :type="inputType"
        :inputmode="inputMode"
        :disabled="disabled"
        :readonly="disabled"
        :required="required"
        autocomplete="new-password"
        :value="value"
        @focus="onFocus"
        @blur="onLostFocus"
        @mouseenter="onHover"
        @mouseleave="onUnHover"
        :aria-label="placeholder"
        :title="placeholder"
        ref="input"
        tabindex="1"
        @keydown="onKeydown"
        @input="onInput($event.target.value.trim())"
        @change="onChange($event.target.value.trim())"
        @paste="onPaste"
      />
    </label>
    <span v-if="exampleMessage" class="example">{{exampleMessage}}</span>
    <span
      class="tooltip-baloon"
      :class="{ active: isFocus }"
      v-if="tooltipText"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        enable-background="new 0 0 24 24"
        height="24"
        viewBox="0 0 24 24"
        width="24"
        class="icon"
      >
        <path
          d="m21.25 0h-18.5c-1.517 0-2.75 1.233-2.75 2.75v18.5c0 1.517 1.233 2.75 2.75 2.75h18.5c1.517 0 2.75-1.233 2.75-2.75v-18.5c0-1.517-1.233-2.75-2.75-2.75z"
          fill="#faebaf"
        />
        <circle cx="12" cy="18" r="1" fill="#111111" />
        <path
          fill="#111111"
          d="m12 15c-.414 0-.75-.336-.75-.75v-8.5c0-.414.336-.75.75-.75s.75.336.75.75v8.5c0 .414-.336.75-.75.75z"
        />
      </svg>
      {{ tooltipText }}</span
    >
    <tooltip :text="tipsText" v-if="tipsText" class="tooltip"></tooltip>
    <div
      v-if="isShowBigTooltip"
      class="smart-input__tooltip"
      @mouseenter="onHover"
      @mouseleave="onUnHover"
    >
      <button
        class="smart-input__tooltip-close"
        @click="closeBigTooltip"
      ></button>
      <div class="smart-input__tooltip-text" v-html="bigTooltipText"></div>
    </div>
    <div class="reset" v-if="notEmpty && !disabled" @click.stop="onClearClick">
      &times;
    </div>
    <div class="errors" v-if="error && isTouch">{{ error }}</div>
  </div>
</template>

<script>
import Inputmask from 'inputmask';
import tooltip from './tooltip.vue';

export default {
  name: 'smartInput',
  model: {
    prop: 'value',
    event: 'input',
  },
  components: {
    tooltip,
  },
  props: {
    type: {
      type: String,
      default: 'text',
    },
    placeholder: {
      type: String,
      default: '',
    },
    auto: {
      type: String,
    },
    value: {
      type: String,
    },
    error: {
      type: String,
    },
    exampleMessage: {
      type: String,
    },
    tipsText: {
      type: String,
    },
    bigTooltipText: {
      type: String,
    },
    tooltipText: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    inn: {
      type: Boolean,
      default: false,
    },
    kpp: {
      type: Boolean,
      default: false,
    },
    typeText: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Number,
      default: null,
    },
    minLength: {
      type: Number,
      default: null,
    },
    enableInputmask: {
      type: Boolean,
      default: false,
    },
    isLatinOnly: {
      type: Boolean,
      default: false,
    },
    isCyrillicOnly: {
      type: Boolean,
      default: false,
    },
    customMask: {
      type: [Object, String, undefined],
      required: false,
      default: undefined,
    },
    onlyInteger: {
      type: Boolean,
      default: false,
    },
    fixedNumber: {
      type: Number,
      default: 2,
    },
    maxNumber: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      isFocus: false,
      isTouch: false,
      isHoverForBigTooltip: false,
      counterForBigTooltip: 0,
      hoverId: null,
      latinOnlyPopupShowed: false,
      im: null,
      isCurrentDecimal: false,
      timeOut: null,
    };
  },
  computed: {
    classList() {
      return {
        'is-focus': this.isFocus,
        'is-error': this.isError && this.isTouch,
        'not-empty': !!this.notEmpty,
        'is-required': this.required && !this.notEmpty,
      };
    },
    latinOnlyPattern() {
      return new RegExp('[А-Яа-я]', 'ig');
    },
    emailPattern() {
      return new RegExp(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/);
    },
    innPattern() {
      return new RegExp('^(?!99)[0-9]{10,12}$');
    },
    kppPattern() {
      return new RegExp('^([0-9]{9})?$');
    },
    notEmpty() {
      return !!this.value;
    },
    isError() {
      return !!this.error;
    },
    isRequired() {
      return this.required;
    },
    isINN() {
      return this.inn;
    },
    isKPP() {
      return this.kpp;
    },
    isEmail() {
      return this.type === 'email';
    },
    isPhone() {
      return this.type === 'tel';
    },
    isNumber() {
      return this.type === 'number';
    },
    isTypeText() {
      return this.typeText;
    },
    texts() {
      return {
        required: this.$t('Required'),
        notCorrectEmail: this.$t('NotCorrectEmail'),
        notCorrectPhone: this.$t('NotCorrectPhone'),
        notCorrectINN: this.$t('NotCorrectINN'),
        notCorrectKPP: this.$t('NotCorrectKPP'),
        notCorrectTypeText: this.$t('NotCorrectDeliveryTypeText'),
        latinOnly: this.$t('LatinOnly'),
        CyrillicOnly: this.$t('CyrillicOnly'),
        latinOnlyPopup: this.$t('LatinOnlyPopup'),
        minLength: this.$t('minTextLength', { number: this.minLength }),
        maxLength: this.$t('maxTextLength', { number: this.maxLength }),
      };
    },
    isEmptyBigTooltip() {
      const isString = typeof this.bigTooltipText === 'string';
      return (isString && this.bigTooltipText.trim() === '') || !isString;
    },
    isShownBigTooltip() {
      return this.counterForBigTooltip !== 0;
    },
    isShowBigTooltip() {
      return (
        !this.isEmptyBigTooltip &&
        (this.isHoverForBigTooltip || this.isFocus) &&
        !this.isShownBigTooltip
      );
    },
    inputType() {
      if (this.enableInputmask !== true) {
        return this.type;
      }
      // because Inputmask plugin don't want to work with input[type=email],
      // it leaves this for a browser
      return this.type === 'email' ? 'text' : this.type;
    },
    inputMode() {
      if (this.enableInputmask !== true) {
        return this.type;
      }
      // because Inputmask plugin don't want to work with input[type=email],
      // it leaves this for a browser
      return this.type === 'email' ? 'email' : '';
    },
  },
  watch: {
    value: {
      handler: 'onValidate',
    },
  },
  created() {
    if (this.required && !this.value) {
      this.validateRequired('');
    }
  },
  methods: {
    onClearClick() {
      this.isFocus = false;
      this.$emit('input', '');
      this.$emit('clear');
      this.onValidate('');
    },
    onKeydown(event) {
      const { isNumber, isPhone } = this;

      if (isNumber) {
        this.onNumberChange(event);
        return;
      }

      if (isPhone) {
        this.onPhoneChange(event);
      }

      if (this.maxLength === null) {
        return;
      }

      if (event.target.value.length >= this.maxLength) {
        const isArrowLeft = event.which === 37;
        const isArrowUp = event.which === 38;
        const isArrowRight = event.which === 39;
        const isArrowDown = event.which === 40;
        const isBackspace = event.which === 8;
        const isCopyPaste =
          (event.metaKey || event.ctrlKey) && [86, 67].includes(event.which);

        if (
          !(
            isArrowLeft ||
            isArrowUp ||
            isArrowRight ||
            isArrowDown ||
            isBackspace ||
            isCopyPaste
          )
        ) {
          event.preventDefault();
        }
      }
    },
    /**
     *
     * Проверка на нужный разделитель дробных чисел
     * */
    validateNumberValue(value) {
      clearTimeout(this.timeOut);
      const isFiniteString = /^\d+[.,]$/.test(value);

      if (!isFiniteString) {
        const result = value.replace(',', '.');
        this.$emit('change', result);
      }
    },
    /**
     *
     * Проверка на символы телефонного номера
     * */
    onPhoneChange($event) {
      const { keyCode } = $event;
      // Allow: copy and paste, backspace, delete, tab, escape, enter,
      // + (only as first char), arrows (left, right),
      // numpad numbers and +
      if (
        ($event.target.selectionStart === 0 &&
          $event.shiftKey &&
          keyCode === 187) ||
        (($event.metaKey || $event.ctrlKey) && [86, 67].includes(keyCode)) ||
        [8, 9, 27, 13, 44, 46, 37, 39, 107].includes(keyCode)
      ) {
        return;
      }

      if (
        $event.shiftKey ||
        ![
          48,
          49,
          50,
          51,
          52,
          53,
          54,
          55,
          56,
          57,
          96,
          97,
          98,
          99,
          100,
          101,
          102,
          103,
          104,
          105,
        ].includes(keyCode)
      ) {
        $event.preventDefault();
      }
    },
    /**
     * Проверка на цифры
     * @param $event
     */
    onNumberChange($event) {
      const { keyCode } = $event;

      if (this.onlyInteger && [44, 46].includes(keyCode)) {
        $event.preventDefault();
      }

      this.isCurrentDecimal = /\d?[.,]\d?/.test(this.currentValue);
      // Allow: copy and paste, backspace, delete, tab, escape, enter and ./,
      // arrows (left, right)
      // numpad numbers and .
      // если уже десятичное число и повторно пытаемся ввести дробную часть
      if (
        ([8, 9, 27, 13, 110, 188, 190, 44, 46, 37, 39].includes(keyCode) &&
          !([44, 46].includes(keyCode) && this.isCurrentDecimal)) ||
        (($event.metaKey || $event.ctrlKey) && [86, 67].includes(keyCode))
      ) {
        return;
      }

      if (
        $event.shiftKey ||
        ![
          48,
          49,
          50,
          51,
          52,
          53,
          54,
          55,
          56,
          57,
          96,
          97,
          98,
          99,
          100,
          101,
          102,
          103,
          104,
          105,
        ].includes(keyCode)
      ) {
        $event.preventDefault();
      }
    },
    onBlur(event) {
      this.validateTextLength(event.target.value);
      this.onValidate(event.target.value);
    },
    onInput(value) {
      this.$emit('user-change-input', value); // отдельная абстракция, которая  может изменится под капотом
      this.$emit('input', value);
      this.onValidate(value);
    },
    onChange(value) {
      this.$emit('change', value);
      this.onValidate(value);
    },
    onPaste(event) {
      event.preventDefault();
      const { isPhone } = this;
      let result = event.clipboardData.getData('text/plain').trim();

      if (isPhone) {
        // удаляем все кроме + и цифр
        result = event.clipboardData
          .getData('text/plain')
          .replace(/[^+\d]+/g, '');
      }

      this.onChange(result);
    },
    onLostFocus(event) {
      this.onBlur(event);

      this.isTouch = true;
      this.counterForBigTooltip = this.counterForBigTooltip + 1;
      if (this.isFocus) {
        this.isFocus = false;
        this.$emit('focusLost');
      }
    },
    onFocus() {
      this.isFocus = true;
      this.onValidate(this.value);
    },
    onValidate(val) {
      const {
        isEmail,
        isINN,
        isKPP,
        isLatinOnly,
        isCyrillicOnly,
        isRequired,
        minLength,
        maxLength,
        isNumber,
        isTypeText,
      } = this;
      const value = val ? val.trim().replace(/(\r\n|\n|\r)/gm, '') : val;

      if (isRequired) {
        this.validateRequired(value);
      } else if (value === '') {
        this.$emit('error', '');
        return;
      }

      if ((minLength || maxLength) && value) {
        this.validateTextLength(value);
      }

      // eslint-disable-next-line default-case
      switch (true) {
        case isEmail: {
          this.validateEmail(value);
          break;
        }
        case isINN: {
          this.validateINN(value);
          break;
        }
        case isKPP: {
          this.validateKPP(value);
          break;
        }
        case isLatinOnly: {
          this.validateLatinOnly(value);
          break;
        }
        case isCyrillicOnly: {
          this.passOnlyCyrillicSymbol(value);
          break;
        }
        case isNumber: {
          this.validateNumberValue(value);
          break;
        }
        case isTypeText: {
          this.validateTypeTextValue(value);
          break;
        }
      }
    },
    validateRequired(value) {
      if (value === '' || value === null || value === '0') {
        this.$emit('error', this.texts.required);
      } else {
        this.$emit('error', '');
      }
    },
    validateEmail(email) {
      const { input } = this.$refs;
      let result = false;
      if (input.inputmask) {
        result = input.inputmask.isComplete();
      } else {
        result = this.emailPattern.test(email);
      }

      if (!result) {
        this.$emit('error', this.texts.notCorrectEmail);
      } else {
        this.$emit('error', '');
      }
    },
    validateINN(inn) {
      if (!this.innPattern.test(inn)) {
        this.$emit('error', this.texts.notCorrectINN);
      } else {
        this.$emit('error', '');
      }
    },
    validateKPP(kpp) {
      if (!this.kppPattern.test(kpp)) {
        this.$emit('error', this.texts.notCorrectKPP);
      } else {
        this.$emit('error', '');
      }
    },
    validateLatinOnly(value) {
      const isLatinOnly = value.search(this.latinOnlyPattern) === -1;
      if (!isLatinOnly) {
        this.$emit('error', this.texts.latinOnly);
        if (this.latinOnlyPopupShowed === false) {
          this.latinOnlyPopupShowed = true;
          this.$emit('popup-error', this.texts.latinOnlyPopup);
        }
      } else {
        this.$emit('error', '');
      }
    },
    validateTextLength(value) {
      if (!value) {
        this.$emit('error', '');
      }

      let text = value;
      const { minLength, maxLength } = this;

      if (text.length > 0 && minLength && minLength > text.length) {
        this.$emit('error', this.texts.minLength);
      } else {
        this.$emit('error', '');
      }

      if (maxLength) {
        if (value.length > maxLength) {
          this.$emit('error', this.texts.maxLength);
          text = text.slice(0, maxLength);
        }

        this.$emit('change', text);
      }
    },
    validateTypeTextValue(typeText) {
      if (!typeText) {
        this.validateRequired(typeText);
        return;
      }

      let val = typeText.toLowerCase();
      const contentDescriptionBlackList = this.$store.getters['common/sessionSettings'].contentDescriptionBlackList.dox;

      contentDescriptionBlackList.forEach(({ contentDescription }) => {
        val = val.replaceAll(contentDescription.toLowerCase(), '');
      });

      val = val.replaceAll('  ', ' ');

      if (val.length / typeText.length < 0.7) {
        this.$emit('error', this.texts.notCorrectTypeText);
      } else {
        this.$emit('error', '');
      }
    },
    passOnlyCyrillicSymbol(value) {
      const isCyrillicOnly = value.search(new RegExp('[A-Za-z]', 'ig')) === -1;
      if (!isCyrillicOnly) {
        this.$emit('error', this.texts.CyrillicOnly);
        if (this.latinOnlyPopupShowed === false) {
          this.latinOnlyPopupShowed = true;
          this.$emit('popup-error', this.texts.CyrillicOnly);
        }
      } else {
        this.$emit('error', '');
      }
    },
    closeBigTooltip() {
      this.counterForBigTooltip = this.counterForBigTooltip + 1;
    },
    onHover() {
      clearTimeout(this.hoverId);
      this.isHoverForBigTooltip = true;
    },
    onUnHover() {
      this.hoverId = setTimeout(() => {
        this.isHoverForBigTooltip = false;
      }, 50);
    },
    setNumberValue(value) {
      let result = value;
      const { onlyInteger, maxNumber, currentDecimal, fixedNumber } = this;

      if (maxNumber && value > maxNumber) {
        result = maxNumber;
      }

      if (onlyInteger) {
        result = parseFloat(result) || 0;
        result = Math.ceil(result);
      } else {
        this.isCurrentDecimal = /\d?\.\d?/.test(result);
        if (currentDecimal) {
          const decimalLength = result.toString().includes('.')
            ? result
                .toString()
                .split('.')
                .pop().length
            : 0;
          result = parseFloat(result) || 0;
          if (decimalLength > 2) {
            result = result.toFixed(fixedNumber);
          } else {
            result = result.toFixed(decimalLength);
          }
        }
      }

      this.currentValue = result;

      this.$emit('input', result);
    },
  },
  mounted() {
    const { input } = this.$refs;

    if (this.type === 'email') {
      this.im = new Inputmask({ alias: 'email' });
      this.im.mask(input);
    }

    if (this.customMask && this.enableInputmask) {
      this.im = new Inputmask(this.customMask);

      this.im.mask(input);
    }
  },
};
</script>
<style scoped lang="scss">
$inputLeftPadding: 24;

.smart-input {
  position: relative;
  width: 100%;
  margin-bottom: 16px;
  border-radius: 4px;
  background-color: #f0f0f0;
  border: 1px solid #f0f0f0;

  transition: border-color 0.25s, background-color 0.25s;

  &.is-error {
    border: 1px solid red;
    background: linear-gradient(-45deg, lighten(red, 45), lighten(red, 30));
    background-size: 200% 200%;
    animation: gradient 2s ease infinite;
  }

  &.is-focus {
    border: 1px solid #ffcc00;
    background-color: #fff;
  }

  &.is-required {
    background-color: lighten(red, 45);
  }

  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  label {
    display: block;
    position: relative;
    cursor: pointer;
    overflow: hidden;
  }

  input {
    display: block;
    width: 100%;
    height: 54px;
    border: 0;
    padding: 20px $inputLeftPadding * 1px 4px;
    margin: 0;
    background-color: transparent;
    outline: none;

    color: #111111;
    font-size: 16px;
    font-weight: 300;
    line-height: 2;
  }

  .tooltip-baloon {
    position: absolute;
    font-size: 12px;
    padding: 10px;
    width: 100%;
    top: calc(100% + 10px);
    background-color: #fff;
    border-radius: 8px;
    position: absolute;
    z-index: 1;
    box-sizing: border-box;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
    display: none;

    .icon {
      margin-right: 10px;
    }

    &.active {
      display: flex;
      align-items: center;
    }

    &::after {
      content: '';
      position: absolute;
      width: 12px;
      height: 12px;
      bottom: 100%;
      left: 15%;
      border-width: 5px;
      border-style: solid;
      border-color: transparent transparent white transparent;
    }
  }

  .tooltip {
    position: absolute;

    + .reset {
      right: 2rem;
    }
  }

  .reset {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translate(0, -50%);
  }

  .errors {
    position: absolute;
  }
}

.smart-input__tooltip {
  position: absolute;
  top: 0;
  right: 0;
  width: auto;
  max-width: 100%;
  padding: 12px;
  color: #c1c1c1;
  font-size: 12px;
  line-height: 1.181818;
  background-color: #262626;
  border-radius: 4px;
  z-index: 1;
  transform: translateY(calc(-100% - 5px));
}

.smart-input__tooltip-close {
  position: absolute;
  top: 5px;
  right: 5px;
  background: none;
  border: 0;
  padding: 0;
  width: 10px;
  height: 10px;
  cursor: pointer;

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 1px;
    background: #ffffff;
  }

  &:before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &:after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
}

.placeholder, .example {
  position: absolute;
  left: $inputLeftPadding * 1px;
  top: 50%;

  transform: translate(0, -50%) scale(1);
  transform-origin: left center;
  transition: transform 0.15s ease;

  color: #888888;
  font-size: 15px;
  font-weight: 300;
  pointer-events: none;
  max-width: calc(100% - #{$inputLeftPadding * 2px});
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .is-focus &,
  .not-empty & {
    transform: translate(0, -100%) scale((11 / 15));
  }

  .is-focus .is-example &,
  .not-empty .is-example & {
    top: 50%;
  }
}

.is-example {
  .placeholder {
    top: 35%;
  }
}

.example {
  font-size: 12px;
  top: 68%;

  .is-focus &,
  .not-empty & {
    display: none;
  }
}

.reset {
  width: 30px;
  height: 30px;

  padding: 2px;

  transition: font-size 0.15s ease;

  color: #ffcc00;
  font-size: 24px;
  line-height: 30px;
  text-align: center;
  cursor: pointer;

  &:hover {
    font-size: 30px;
  }
}

.required {
  color: red;
}
</style>
